import React, { useState, useEffect, useRef } from 'react';
import { leadFilterAPI, categoryAPI, locationAPI, leadPricingAPI, vendorProfileAPI } from '../../services/api';
import { FormSkeleton } from '../../components/skeletons';
import { useAuth } from '../../context/AuthContext';

const S = {
  page: { padding: '24px 20px', background: 'var(--cream)', minHeight: '100vh' },
  title: { fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 700, color: 'var(--char)', marginBottom: 4 },
  subtitle: { fontSize: 13, color: 'var(--muted)', marginBottom: 20 },
  card: { padding: 20, borderRadius: 14, background: '#fff', border: '1.5px solid var(--border)', marginBottom: 16, boxShadow: 'var(--shadow-sm)', transition: 'all 0.2s ease' },
  tag: { display: 'inline-block', padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, marginRight: 6, marginBottom: 4 },
  formCard: { padding: '24px 20px', borderRadius: 14, background: '#fff', border: '2.5px solid var(--green)', marginBottom: 24, boxShadow: 'var(--shadow)' },
  formTitle: { fontSize: 18, fontWeight: 700, color: 'var(--char)', marginBottom: 16, fontFamily: 'var(--font-serif)' },
  formRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--char)', marginBottom: 4 },
  select: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13, background: '#fff', outline: 'none' },
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13, boxSizing: 'border-box', outline: 'none' },
  toggle: (active) => ({
    width: 44, height: 24, borderRadius: 12, cursor: 'pointer', border: 'none',
    background: active ? '#1B5C3A' : '#ccc', position: 'relative', transition: 'background 0.2s',
  }),
  toggleDot: (active) => ({
    width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3,
    left: active ? 23 : 3, transition: 'left 0.2s',
  }),
  emptyBox: { textAlign: 'center', padding: '48px 20px', background: '#fff', borderRadius: 14, border: '1.5px solid var(--border)' },
};

export default function LeadFilters() {
  const { user } = useAuth();
  const [filters, setFilters] = useState([]);
  const [catTree, setCatTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendorProfile, setVendorProfile] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    category: '', subcategory: '', brand: '',
    budgetMin: '', budgetMax: '',
    geo: 'city', additionalCities: [], dailyCap: '', monthlyCap: '',
  });
  const [flatRate, setFlatRate] = useState(null);
  const isFinancial = vendorProfile?.vendorType === 'financial';
  const [allLocations, setAllLocations] = useState([]);
  const [leadCost, setLeadCost] = useState(null);
  const [allPricing, setAllPricing] = useState([]);
  const [auditModal, setAuditModal] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [filterStats, setFilterStats] = useState({});

  const loadStats = () => {
    leadFilterAPI.stats().then(r => setFilterStats(r.data.data || {})).catch(() => { });
  };

  const dropdownRef = useRef(null);
  const editDropdownRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [cityFilter, setCityFilter] = useState('');
  const [editDropdownOpen, setEditDropdownOpen] = useState(false);
  const [editCityFilter, setEditCityFilter] = useState('');

  useEffect(() => {
    Promise.all([
      leadFilterAPI.list().then(r => setFilters(r.data.data || [])).catch(() => { }),
      categoryAPI.tree().then(r => setCatTree(r.data.data || [])).catch(() => { }),
      locationAPI.all().then(r => setAllLocations(r.data.data || [])).catch(() => { }),
      leadPricingAPI.list().then(r => setAllPricing(r.data.data || [])).catch(() => { }),
      leadFilterAPI.stats().then(r => setFilterStats(r.data.data || {})).catch(() => { }),
      vendorProfileAPI.me().then(r => setVendorProfile(r.data.data || null)).catch(() => { }),
      leadPricingAPI.getFinancialRate().then(r => setFlatRate(r.data.data?.rate ?? null)).catch(() => { }),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (editDropdownRef.current && !editDropdownRef.current.contains(event.target)) {
        setEditDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);



  const subcategories = form.category
    ? (catTree.find(c => c._id === form.category)?.subcategories || [])
    : [];
  const brands = form.subcategory
    ? (subcategories.find(s => s._id === form.subcategory)?.brands || [])
    : [];

  const firmState = vendorProfile?.state || user?.state || '';
  const firmCity = vendorProfile?.city || user?.city || '';
  const stateLoc = allLocations.find(l => l.state?.toLowerCase() === firmState.toLowerCase());
  const stateCities = stateLoc ? stateLoc.cities : [];

  // Helper: find pricing for a filter card
  const getFilterCost = (f) => {
    const catId = f.categoryId?._id || f.categoryId;
    const subId = f.subCategoryId?._id || f.subCategoryId;
    const brandId = f.brandId?._id || f.brandId;
    // Most specific wins
    let match = allPricing.find(r => String(r.categoryId?._id || r.categoryId) === String(catId) && String(r.subCategoryId?._id || r.subCategoryId || '') === String(subId || '') && String(r.brandId?._id || r.brandId || '') === String(brandId || ''));
    if (!match && subId) match = allPricing.find(r => String(r.categoryId?._id || r.categoryId) === String(catId) && String(r.subCategoryId?._id || r.subCategoryId || '') === String(subId || '') && !r.brandId);
    if (!match) match = allPricing.find(r => String(r.categoryId?._id || r.categoryId) === String(catId) && !r.subCategoryId && !r.brandId);
    if (match) return { local: match.localRate, outCity: match.outCityRate || Math.round(match.localRate * 0.5), found: true, rule: match };
    return { local: 0, outCity: 0, found: false, rule: null };
  };

  // Fetch lead cost when category/sub/brand changes
  useEffect(() => {
    if (!form.category) { setLeadCost(null); return; }
    const params = { categoryId: form.category };
    if (form.subcategory) params.subCategoryId = form.subcategory;
    if (form.brand) params.brandId = form.brand;
    // Fetch both local and out-of-city rates
    Promise.all([
      leadPricingAPI.getRate({ ...params, isOutOfCity: 'false' }),
      leadPricingAPI.getRate({ ...params, isOutOfCity: 'true' }),
    ]).then(([localRes, outRes]) => {
      setLeadCost({
        local: localRes.data.data?.rate || 0,
        outCity: outRes.data.data?.rate || 0,
        hasRule: !!localRes.data.data?.rule,
        rule: localRes.data.data?.rule || null,
      });
    }).catch(() => setLeadCost(null));
  }, [form.category, form.subcategory, form.brand]);

  const handleChange = (field, val) => {
    const next = { ...form, [field]: val };
    if (field === 'category') { next.subcategory = ''; next.brand = ''; }
    if (field === 'subcategory') { next.brand = ''; }
    setForm(next);
  };

  const handleSave = async () => {
    if (!form.category) return;
    setSaving(true);
    try {
      const scope = leadCost?.rule?.leadReceivingFrom || 'home_state';
      const payload = {
        categoryId: form.category,
        subCategoryId: form.subcategory || undefined,
        brandId: form.brand || undefined,
        minBudget: form.budgetMin ? Number(form.budgetMin) : 0,
        maxBudget: form.budgetMax ? Number(form.budgetMax) : 99999999,
        geo: scope === 'home_state' && form.additionalCities.length > 0 ? 'custom' : 'city',
        additionalCities: scope === 'home_state' ? form.additionalCities : [],
        dailyCap: form.dailyCap ? Number(form.dailyCap) : 0,
        monthlyCap: form.monthlyCap ? Number(form.monthlyCap) : 0,
      };
      const res = await leadFilterAPI.create(payload);
      setFilters([...filters, res.data.data]);
      setForm({ category: '', subcategory: '', brand: '', budgetMin: '', budgetMax: '', geo: 'city', additionalCities: [], dailyCap: '', monthlyCap: '' });
      setShowForm(false);
      loadStats();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save filter');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (f) => {
    setEditingId(f._id);
    setEditForm({
      minBudget: f.minBudget || '',
      maxBudget: f.maxBudget >= 99999999 ? '' : (f.maxBudget || ''),
      dailyCap: f.dailyCap || '',
      monthlyCap: f.monthlyCap || '',
      additionalCities: f.additionalCities || [],
    });
  };

  const handleEditSave = async () => {
    setEditSaving(true);
    try {
      const f = filters.find(item => item._id === editingId);
      const costInfo = f ? getFilterCost(f) : { rule: null };
      const scope = costInfo.rule?.leadReceivingFrom || 'home_state';
      const payload = {
        minBudget: editForm.minBudget ? Number(editForm.minBudget) : 0,
        maxBudget: editForm.maxBudget ? Number(editForm.maxBudget) : 99999999,
        dailyCap: editForm.dailyCap ? Number(editForm.dailyCap) : 0,
        monthlyCap: editForm.monthlyCap ? Number(editForm.monthlyCap) : 0,
        additionalCities: scope === 'home_state' ? editForm.additionalCities : [],
        geo: scope === 'home_state' && editForm.additionalCities?.length > 0 ? 'custom' : 'city',
      };
      const res = await leadFilterAPI.update(editingId, payload);
      setFilters(filters.map(item => item._id === editingId ? { ...item, ...res.data.data } : item));
      setEditingId(null);
      loadStats();
    } catch (err) { alert(err.response?.data?.message || 'Failed.'); }
    finally { setEditSaving(false); }
  };

  const handleToggle = async (id) => {
    try {
      await leadFilterAPI.toggle(id);
      setFilters(filters.map(f => f._id === id ? { ...f, isActive: !f.isActive } : f));
      loadStats();
    } catch (err) {
      alert('Failed to toggle filter');
    }
  };

  const handleRemove = async (id) => {
    try {
      await leadFilterAPI.remove(id);
      setFilters(filters.filter(f => f._id !== id));
      loadStats();
    } catch (err) {
      alert('Failed to remove filter');
    }
  };

  if (loading) {
    return <FormSkeleton fields={5} style={{ margin: '24px 20px' }} />;
  }

  return (
    <div style={S.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
        <div>
          <div style={S.title}>Lead Filters</div>
          <div style={S.subtitle}>Configure which leads you want to receive</div>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{
            background: showForm ? 'var(--muted)' : 'var(--green)', color: '#fff', border: 'none',
            padding: '10px 22px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
            transition: 'background-color 0.2s', boxShadow: 'var(--shadow-sm)'
          }}>
          {showForm ? 'Cancel' : '+ Add Filter'}
        </button>
      </div>

      {/* Add Filter Form */}
      {showForm && (
        <div style={S.formCard}>
          <div style={S.formTitle}>New Lead Filter</div>

          {/* Cascading Selects */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={S.label}>Category *</label>
              <select style={S.select} value={form.category} onChange={e => handleChange('category', e.target.value)}>
                <option value="">Select category</option>
                {catTree.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Subcategory</label>
              <select style={S.select} value={form.subcategory} onChange={e => handleChange('subcategory', e.target.value)} disabled={!form.category}>
                <option value="">All subcategories</option>
                {subcategories.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Brand</label>
              <select style={S.select} value={form.brand} onChange={e => handleChange('brand', e.target.value)} disabled={!form.subcategory}>
                <option value="">All brands</option>
                {brands.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
              </select>
            </div>
          </div>

          {/* Lead Cost Display */}
          {form.category && (
            isFinancial ? (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '14px 18px', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#1B5C3A', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                    🏦 Flat Lead Rate for Financial Vendors
                  </div>
                  <div style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>
                    {catTree.find(c => c._id === form.category)?.name}
                    {form.subcategory && ` › ${subcategories.find(s => s._id === form.subcategory)?.name}`}
                    {form.brand && ` › ${brands.find(b => b._id === form.brand)?.name}`}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, color: '#1B5C3A' }}>
                    {flatRate !== null ? `₹${flatRate}` : 'Loading...'}
                  </div>
                  <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600 }}>Flat Rate per Matched Lead</div>
                </div>
              </div>
            ) : (
              <div style={{ background: leadCost?.hasRule ? '#f0fdf4' : '#fef3c7', border: `1px solid ${leadCost?.hasRule ? '#bbf7d0' : '#fde68a'}`, borderRadius: 10, padding: '14px 18px', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: leadCost?.hasRule ? '#1B5C3A' : '#92400e', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                    {leadCost?.hasRule ? '💲 Lead Cost for this combination' : '⚠️ No pricing set by admin'}
                  </div>
                  <div style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>
                    {catTree.find(c => c._id === form.category)?.name}
                    {form.subcategory && ` › ${subcategories.find(s => s._id === form.subcategory)?.name}`}
                    {form.brand && ` › ${brands.find(b => b._id === form.brand)?.name}`}
                  </div>
                </div>
                {leadCost && (
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, color: '#1B5C3A' }}>₹{leadCost.local}</div>
                      <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600 }}>Home City</div>
                    </div>
                    <div style={{ width: 1, height: 32, background: '#e5e7eb' }} className="hide-mobile" />
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, color: '#C4831A' }}>₹{leadCost.outCity}</div>
                      <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600 }}>Other Cities (50%)</div>
                    </div>
                  </div>
                )}
              </div>
            )
          )}

          {/* Budget Range */}
          {!isFinancial && (
            <div style={S.formRow}>
              <div>
                <label style={S.label}>Min Budget</label>
                <input style={S.input} className="form-input" type="number" placeholder="0" value={form.budgetMin} onChange={e => handleChange('budgetMin', e.target.value)} onWheel={(e) => e.target.blur()} />
              </div>
              <div>
                <label style={S.label}>Max Budget</label>
                <input style={S.input} className="form-input" type="number" placeholder="No limit" value={form.budgetMax} onChange={e => handleChange('budgetMax', e.target.value)} onWheel={(e) => e.target.blur()} />
              </div>
            </div>
          )}

          {/* Geo — City Picker */}
          {!isFinancial && (leadCost?.rule?.leadReceivingFrom || 'home_state') === 'home_state' && (
            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>Lead Cities</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                <div style={{ background: '#FFF8E7', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', fontSize: 15, color: '#C4831A', lineHeight: 1.4 }}>
                  📍 You will automatically receive leads from your home city. If you would like to receive leads from other cities across your state, please update your selections above.
                </div>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', fontSize: 15, color: '#1B5C3A', lineHeight: 1.4 }}>
                  🏠 From your home city <strong>{firmCity || 'Your home city'}</strong>: leads from here are always included at full rate
                </div>
                <div style={{ background: '#FFF8E7', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', fontSize: 15, color: '#C4831A', lineHeight: 1.4 }}>
                  🌍 For outside cities selected: leads from here are always included at half rate
                </div>
              </div>

              {/* Selected cities */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {form.additionalCities.map(city => (
                  <span key={city} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#FFF8E7', color: '#C4831A', padding: '4px 10px', borderRadius: 16, fontSize: 15, fontWeight: 600 }}>
                    {city} <span style={{ fontSize: 13, color: '#C4831A', opacity: 0.6 }}>50%</span>
                    <button onClick={() => {
                      const nextCities = form.additionalCities.filter(c => c !== city);
                      setForm({ ...form, additionalCities: nextCities, geo: nextCities.length > 0 ? 'custom' : 'city' });
                    }} style={{ background: 'none', border: 'none', color: '#C4831A', cursor: 'pointer', fontSize: 14, padding: 0, marginLeft: 2 }}>×</button>
                  </span>
                ))}
              </div>

              {/* Dropdown Checklist Selector */}
              <div style={{ position: 'relative', marginBottom: 4 }} ref={dropdownRef}>
                <div
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: 8,
                    border: '1.5px solid var(--border)',
                    fontSize: 13,
                    background: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    boxSizing: 'border-box'
                  }}
                >
                  <span style={{ color: form.additionalCities.length > 0 ? 'var(--char)' : '#9ca3af' }}>
                    {form.additionalCities.length > 0
                      ? `Selected outside cities: ${form.additionalCities.join(', ')}`
                      : 'Select outside cities from your state...'}
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--muted)' }}>{dropdownOpen ? '▲' : '▼'}</span>
                </div>

                {dropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '105%',
                    left: 0,
                    right: 0,
                    background: '#fff',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: 8,
                    boxShadow: 'var(--shadow-lg)',
                    zIndex: 200,
                    maxHeight: 280,
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <div style={{ padding: 8, borderBottom: '1px solid #f3f4f6' }}>
                      <input
                        type="text"
                        placeholder="Search cities in state..."
                        value={cityFilter}
                        onChange={e => setCityFilter(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        style={{
                          width: '100%',
                          padding: '6px 10px',
                          borderRadius: 6,
                          border: '1px solid #e5e7eb',
                          fontSize: 12,
                          boxSizing: 'border-box',
                          outline: 'none'
                        }}
                      />
                    </div>

                    <div style={{ overflowY: 'auto', padding: '4px 0', textAlign: 'left' }}>
                      {stateCities.filter(c => c.toLowerCase().includes(cityFilter.toLowerCase())).length > 0 ? (
                        stateCities.filter(c => c.toLowerCase().includes(cityFilter.toLowerCase())).map(city => {
                          const isHomeCity = city.toLowerCase() === firmCity.toLowerCase();
                          const isChecked = isHomeCity || form.additionalCities.includes(city);
                          return (
                            <label
                              key={city}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '8px 12px',
                                cursor: isHomeCity ? 'not-allowed' : 'pointer',
                                fontSize: 13,
                                background: isChecked ? '#f0fdf4' : '#fff',
                                transition: 'background 0.1s',
                                userSelect: 'none'
                              }}
                              onMouseEnter={e => { if (!isHomeCity) e.currentTarget.style.background = '#f9fafb'; }}
                              onMouseLeave={e => { if (!isHomeCity) e.currentTarget.style.background = isChecked ? '#f0fdf4' : '#fff'; }}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={isHomeCity}
                                onChange={() => {
                                  if (isHomeCity) return;
                                  const isAlreadyAdded = form.additionalCities.includes(city);
                                  const nextCities = isAlreadyAdded
                                    ? form.additionalCities.filter(c => c !== city)
                                    : [...form.additionalCities, city];
                                  setForm({
                                    ...form,
                                    additionalCities: nextCities,
                                    geo: nextCities.length > 0 ? 'custom' : 'city'
                                  });
                                }}
                                style={{ width: 15, height: 15, accentColor: 'var(--green)', cursor: isHomeCity ? 'not-allowed' : 'pointer' }}
                              />
                              <span style={{ fontWeight: isHomeCity ? 700 : 500, color: isHomeCity ? '#1B5C3A' : 'var(--char)' }}>
                                {city} {isHomeCity ? '(Home City — Always Included)' : ''}
                              </span>
                            </label>
                          );
                        })
                      ) : (
                        <div style={{ padding: 12, textAlign: 'center', color: 'var(--muted)', fontSize: 12 }}>
                          No cities found in your state "{firmState}"
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, fontWeight: 500 }}>
                Home city: full lead cost · Selected state cities: 50% lead cost · {stateCities.length} cities available in {firmState || 'your state'}
              </div>
            </div>
          )}

          {/* Caps */}
          {!isFinancial && (
            <div style={S.formRow}>
              <div>
                <label style={S.label}>Daily Spend Cap (₹)</label>
                <input style={S.input} className="form-input" type="number" min="0" placeholder="Leave blank = Unlimited" value={form.dailyCap} onChange={e => handleChange('dailyCap', e.target.value)} onWheel={(e) => e.target.blur()} />
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 3 }}>{form.dailyCap ? `Max ₹${Number(form.dailyCap).toLocaleString('en-IN')}/day` : 'No limit — receive all matching leads'}</div>
              </div>
              <div>
                <label style={S.label}>Monthly Spend Cap (₹)</label>
                <input style={S.input} className="form-input" type="number" min="0" placeholder="Leave blank = Unlimited" value={form.monthlyCap} onChange={e => handleChange('monthlyCap', e.target.value)} onWheel={(e) => e.target.blur()} />
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 3 }}>{form.monthlyCap ? `Max ₹${Number(form.monthlyCap).toLocaleString('en-IN')}/month` : 'No limit — receive all matching leads'}</div>
              </div>
            </div>
          )}

          <button
            onClick={handleSave}
            disabled={saving || !form.category}
            style={{
              background: 'var(--green)', color: '#fff', border: 'none', padding: '11px 24px',
              borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1, marginTop: 12,
              boxShadow: 'var(--shadow-sm)'
            }}>
            {saving ? 'Saving...' : 'Save Filter'}
          </button>
        </div>
      )}

      {/* Filter Cards */}
      {filters.length === 0 && !showForm ? (
        <div style={S.emptyBox}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🔍</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--char)', marginBottom: 4 }}>No lead filters yet</div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Add filters to start receiving targeted leads</div>
        </div>
      ) : (
        filters.map(f => (
          <div key={f._id} className="card" style={{ ...S.card, opacity: f.isActive === false ? 0.6 : 1 }}
            onMouseEnter={(e) => {
              if (f.isActive !== false) {
                e.currentTarget.style.borderColor = 'var(--green)';
                e.currentTarget.style.boxShadow = 'var(--shadow)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                {/* Tags */}
                <div style={{ marginBottom: 10, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                  {(f.categoryId?.name || f.category?.name) && <span style={{ ...S.tag, background: '#E8F5EE', color: '#1B5C3A', marginBottom: 0 }}>{f.categoryId?.name || f.category?.name}</span>}
                  {(f.subCategoryId?.name || f.subcategory?.name) && <span style={{ ...S.tag, background: '#FFF8E7', color: '#C4831A', marginBottom: 0 }}>{f.subCategoryId?.name || f.subcategory?.name}</span>}
                  {(f.brandId?.name || f.brand?.name) && <span style={{ ...S.tag, background: '#F3F4F6', color: 'var(--char)', marginBottom: 0 }}>{f.brandId?.name || f.brand?.name}</span>}
                  {isFinancial ? (
                    <span style={{ ...S.tag, background: '#f0fdf4', color: '#1B5C3A', fontSize: 10, marginBottom: 0 }}>
                      🏦 Flat rate: {flatRate !== null ? `₹${flatRate}` : '...'}
                    </span>
                  ) : (
                    (() => {
                      const c = getFilterCost(f);
                      const scope = c.rule?.leadReceivingFrom || 'home_state';
                      return c.found ? (
                        <span style={{ ...S.tag, background: '#f0fdf4', color: '#1B5C3A', fontSize: 10, marginBottom: 0 }}>
                          {scope === 'home_state' ? (
                            `💲 ₹${c.local} local · ₹${c.outCity} other`
                          ) : (
                            `💲 ₹${c.local} flat`
                          )}
                        </span>
                      ) : (
                        <span style={{ ...S.tag, background: '#fef3c7', color: '#92400e', fontSize: 10, marginBottom: 0 }}>
                          ⚠ Pricing not set
                        </span>
                      );
                    })()
                  )}
                </div>
                {/* Details */}
                <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--muted)', flexWrap: 'wrap', alignItems: 'center', fontWeight: 500 }}>
                  {!isFinancial && (f.minBudget || f.maxBudget) && (
                    <span>💰 Budget: ₹{(f.minBudget || 0).toLocaleString('en-IN')} – {f.maxBudget < 99999999 ? `₹${f.maxBudget?.toLocaleString('en-IN')}` : 'No limit'}</span>
                  )}
                  {isFinancial ? (
                    <span>🌍 Geographic Scope: All India</span>
                  ) : (
                    (() => {
                      const c = getFilterCost(f);
                      const scope = c.rule?.leadReceivingFrom || 'home_state';
                      if (scope === 'home_city') {
                        return <span>🏠 Home City Only ({firmCity || user?.city || 'Home'})</span>;
                      } else if (scope === 'nation_wide') {
                        return <span>🌍 Nationwide (All Cities)</span>;
                      } else {
                        return (
                          <>
                            <span>🏠 {firmCity || user?.city || 'Home'} (full rate)</span>
                            {f.additionalCities?.length > 0 && (
                              <span>+ {f.additionalCities.map(c => <span key={c} style={{ background: '#FFF8E7', color: '#C4831A', padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 700, marginLeft: 3 }}>{c} 50%</span>)}</span>
                            )}
                          </>
                        );
                      }
                    })()
                  )}
                  {!isFinancial && (
                    <>
                      <span>Daily: {f.dailyCap ? `₹${f.dailyCap.toLocaleString('en-IN')}` : '∞ Unlimited'}</span>
                      <span>Monthly: {f.monthlyCap ? `₹${f.monthlyCap.toLocaleString('en-IN')}` : '∞ Unlimited'}</span>
                    </>
                  )}
                </div>

                {/* Inline Edit Form */}
                {editingId === f._id && (
                  <div style={{ marginTop: 14, background: '#FEFCE8', border: '1.5px solid #FDE68A', borderRadius: 10, padding: 16, boxShadow: 'var(--shadow-sm)' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', marginBottom: 10 }}>✏️ Edit Filter Settings</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 12 }}>
                      <div>
                        <label style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 4 }}>Min Budget (₹)</label>
                        <input type="number" value={editForm.minBudget} onChange={e => setEditForm({ ...editForm, minBudget: e.target.value })}
                          style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} placeholder="0" />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 4 }}>Max Budget (₹)</label>
                        <input type="number" value={editForm.maxBudget} onChange={e => setEditForm({ ...editForm, maxBudget: e.target.value })}
                          style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} placeholder="Unlimited" />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 4 }}>Daily Cap (₹)</label>
                        <input type="number" value={editForm.dailyCap} onChange={e => setEditForm({ ...editForm, dailyCap: e.target.value })}
                          style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} placeholder="Unlimited" />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 4 }}>Monthly Cap (₹)</label>
                        <input type="number" value={editForm.monthlyCap} onChange={e => setEditForm({ ...editForm, monthlyCap: e.target.value })}
                          style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} placeholder="Unlimited" />
                      </div>
                    </div>

                    {/* Edit cities */}
                    {(() => {
                      const costInfo = getFilterCost(f);
                      const scope = costInfo.rule?.leadReceivingFrom || 'home_state';
                      if (scope !== 'home_state') return null;
                      return (
                        <div style={{ marginBottom: 14 }}>
                          <label style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 6 }}>Additional Cities (50% rate)</label>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                            {editForm.additionalCities?.map(city => (
                              <span key={city} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: '#FFF8E7', color: '#C4831A', padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
                                {city} <span style={{ fontSize: 9, color: '#C4831A', opacity: 0.6 }}>50%</span>
                                <button onClick={() => setEditForm({ ...editForm, additionalCities: editForm.additionalCities.filter(c => c !== city) })}
                                  style={{ background: 'none', border: 'none', color: '#C4831A', cursor: 'pointer', fontSize: 13, padding: 0 }}>×</button>
                              </span>
                            ))}
                          </div>

                          {/* Dropdown Checklist Selector for Edit */}
                          <div style={{ position: 'relative' }} ref={editDropdownRef}>
                            <div
                              onClick={() => setEditDropdownOpen(!editDropdownOpen)}
                              style={{
                                width: '100%',
                                padding: '8px 10px',
                                borderRadius: 6,
                                border: '1.5px solid #e5e7eb',
                                fontSize: 12,
                                background: '#fff',
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                boxSizing: 'border-box'
                              }}
                            >
                              <span style={{ color: editForm.additionalCities?.length > 0 ? 'var(--char)' : '#9ca3af' }}>
                                {editForm.additionalCities?.length > 0
                                  ? `Selected outside cities: ${editForm.additionalCities.join(', ')}`
                                  : 'Select outside cities from your state...'}
                              </span>
                              <span style={{ fontSize: 9, color: 'var(--muted)' }}>{editDropdownOpen ? '▲' : '▼'}</span>
                            </div>

                            {editDropdownOpen && (
                              <div style={{
                                position: 'absolute',
                                top: '105%',
                                left: 0,
                                right: 0,
                                background: '#fff',
                                border: '1.5px solid #e5e7eb',
                                borderRadius: 6,
                                boxShadow: 'var(--shadow-lg)',
                                zIndex: 200,
                                maxHeight: 180,
                                display: 'flex',
                                flexDirection: 'column'
                              }}>
                                <div style={{ padding: 6, borderBottom: '1px solid #f3f4f6' }}>
                                  <input
                                    type="text"
                                    placeholder="Search cities in state..."
                                    value={editCityFilter}
                                    onChange={e => setEditCityFilter(e.target.value)}
                                    onClick={e => e.stopPropagation()}
                                    style={{
                                      width: '100%',
                                      padding: '5px 8px',
                                      borderRadius: 4,
                                      border: '1px solid #e5e7eb',
                                      fontSize: 11,
                                      boxSizing: 'border-box',
                                      outline: 'none'
                                    }}
                                  />
                                </div>

                                <div style={{ overflowY: 'auto', padding: '3px 0', textAlign: 'left' }}>
                                  {stateCities.filter(c => c.toLowerCase().includes(editCityFilter.toLowerCase())).length > 0 ? (
                                    stateCities.filter(c => c.toLowerCase().includes(editCityFilter.toLowerCase())).map(city => {
                                      const isHomeCity = city.toLowerCase() === firmCity.toLowerCase();
                                      const isChecked = isHomeCity || editForm.additionalCities?.includes(city);
                                      return (
                                        <label
                                          key={city}
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            padding: '6px 10px',
                                            cursor: isHomeCity ? 'not-allowed' : 'pointer',
                                            fontSize: 12,
                                            background: isChecked ? '#f0fdf4' : '#fff',
                                            transition: 'background 0.1s',
                                            userSelect: 'none'
                                          }}
                                          onMouseEnter={e => { if (!isHomeCity) e.currentTarget.style.background = '#f9fafb'; }}
                                          onMouseLeave={e => { if (!isHomeCity) e.currentTarget.style.background = isChecked ? '#f0fdf4' : '#fff'; }}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isChecked}
                                            disabled={isHomeCity}
                                            onChange={() => {
                                              if (isHomeCity) return;
                                              const isAlreadyAdded = editForm.additionalCities?.includes(city);
                                              const nextCities = isAlreadyAdded
                                                ? editForm.additionalCities.filter(c => c !== city)
                                                : [...(editForm.additionalCities || []), city];
                                              setEditForm({
                                                ...editForm,
                                                additionalCities: nextCities
                                              });
                                            }}
                                            style={{ width: 13, height: 13, accentColor: 'var(--green)', cursor: isHomeCity ? 'not-allowed' : 'pointer' }}
                                          />
                                          <span style={{ fontWeight: isHomeCity ? 700 : 500, color: isHomeCity ? '#1B5C3A' : 'var(--char)' }}>
                                            {city} {isHomeCity ? '(Home City — Always Included)' : ''}
                                          </span>
                                        </label>
                                      );
                                    })
                                  ) : (
                                    <div style={{ padding: 10, textAlign: 'center', color: 'var(--muted)', fontSize: 11 }}>
                                      No cities found in your state "{firmState}"
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={handleEditSave} disabled={editSaving}
                        style={{ background: '#1B5C3A', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 20px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        {editSaving ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button onClick={() => setEditingId(null)}
                        style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 6, padding: '8px 16px', fontSize: 12, cursor: 'pointer', color: '#6b7280', fontWeight: 600 }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Lead Stats */}
                {(() => {
                  const s = filterStats[f._id];
                  if (!s) return null;
                  return (
                    <div style={{ marginTop: 14, borderTop: '1.5px solid #f3f4f6', paddingTop: 14 }}>
                      {/* Leads received */}
                      <div style={{ fontSize: 10, fontWeight: 750, color: '#1B5C3A', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>✓ Leads Received</div>
                      <div style={{ display: 'flex', gap: 0, marginBottom: 12, flexWrap: 'wrap', background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0', padding: 4 }}>
                        {[
                          { label: 'This Week', val: s.thisWeek, color: '#1B5C3A' },
                          { label: 'This Month', val: s.thisMonth, color: '#F5A623' },
                          { label: 'Last Month', val: s.lastMonth, color: '#6b7280' },
                          { label: 'This Year', val: s.thisYear, color: '#4C1D95' },
                          { label: 'All Time', val: s.total, color: '#1a1a1a' },
                        ].map((stat, i) => (
                          <div key={i} style={{ flex: '1 1 90px', textAlign: 'center', padding: '6px 4px', borderRight: i < 4 ? '1px solid #eee' : 'none' }}>
                            <div style={{ fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 700, color: stat.color }}>{stat.val}</div>
                            <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>{stat.label}</div>
                          </div>
                        ))}
                      </div>

                      {/* Missed leads */}
                      {s.missed && (
                        <div style={{ background: s.missed.total > 0 ? '#FEF2F2' : '#F9FAF9', border: `1.5px solid ${s.missed.total > 0 ? '#FECACA' : '#E5E7EB'}`, borderRadius: 10, padding: '12px 16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                            <span style={{ fontSize: 10, fontWeight: 750, color: s.missed.total > 0 ? '#dc2626' : '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.missed.total > 0 ? '✕' : 'ℹ'} Missed Leads (After Filter Set)</span>
                            <span style={{ fontSize: 11, color: s.missed.total > 0 ? '#dc2626' : '#1B5C3A', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setShowForm(true)}>
                              {s.missed.total > 0 ? 'Add cities →' : '✓ Covered'}
                            </span>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10 }}>
                            <div style={{ textAlign: 'center', padding: '4px 0', borderRight: '1px solid rgba(0,0,0,0.05)' }}>
                              <div style={{ fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 700, color: s.missed.total > 0 ? '#dc2626' : '#9ca3af' }}>{s.missed.total}</div>
                              <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 700 }}>Missed (All Time)</div>
                            </div>
                            <div style={{ textAlign: 'center', padding: '4px 0', borderRight: '1px solid rgba(0,0,0,0.05)' }}>
                              <div style={{ fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 700, color: s.missed.thisMonth > 0 ? '#F5A623' : '#9ca3af' }}>{s.missed.thisMonth}</div>
                              <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 700 }}>Missed This Month</div>
                            </div>
                            <div style={{ textAlign: 'center', padding: '4px 0', borderRight: '1px solid rgba(0,0,0,0.05)' }}>
                              <div style={{ fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 700, color: s.missed.inState > 0 ? '#7c3aed' : '#9ca3af' }}>{s.missed.inState}</div>
                              <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 700 }}>In {firmState || 'State'}</div>
                            </div>
                            <div style={{ textAlign: 'center', padding: '4px 0' }}>
                              <div style={{ fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 700, color: s.missed.outState > 0 ? '#6b7280' : '#9ca3af' }}>{s.missed.outState}</div>
                              <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 700 }}>Rest of India</div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {!isFinancial && (
                  <button onClick={() => editingId === f._id ? setEditingId(null) : startEdit(f)}
                    style={{ background: editingId === f._id ? '#fef3c7' : 'none', border: '1.5px solid #e5e7eb', borderRadius: 6, padding: '5px 12px', fontSize: 11, cursor: 'pointer', color: editingId === f._id ? '#d97706' : '#6b7280', fontWeight: 700 }}>
                    {editingId === f._id ? '✕ Cancel' : '✏️ Edit'}
                  </button>
                )}
                <button onClick={async () => {
                  setAuditLoading(true);
                  try {
                    const res = await leadFilterAPI.audit(f._id);
                    setAuditModal({ filterId: f._id, logs: res.data.data || [] });
                  } catch { setAuditModal({ filterId: f._id, logs: [] }); }
                  setAuditLoading(false);
                }} disabled={auditLoading} style={{ background: 'none', border: '1.5px solid #e5e7eb', borderRadius: 6, padding: '5px 12px', fontSize: 11, cursor: 'pointer', color: '#6b7280', fontWeight: 700, opacity: auditLoading ? 0.6 : 1 }}>
                  {auditLoading ? '⌛ Loading...' : '📋 History'}
                </button>
                <button style={S.toggle(f.isActive !== false)} onClick={() => handleToggle(f._id)}>
                  <div style={S.toggleDot(f.isActive !== false)} />
                </button>
                <button onClick={() => handleRemove(f._id)}
                  style={{ background: 'none', border: 'none', color: '#DC2626', fontSize: 16, cursor: 'pointer', padding: 4, fontWeight: 700 }}>
                  ✕
                </button>
              </div>
            </div>
          </div>
        ))
      )}

      {/* Audit Trail Modal */}
      {auditModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setAuditModal(null)}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '24px 28px', width: '90%', maxWidth: '600px', maxHeight: '85vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 700, color: 'var(--char)' }}>📋 Change History</h3>
              <button onClick={() => setAuditModal(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280', fontWeight: 700 }}>✕</button>
            </div>

            {auditModal.logs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af', fontSize: 13 }}>No changes recorded yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {auditModal.logs.map((log, i) => (
                  <div key={i} style={{ background: '#f9fafb', borderRadius: 10, padding: '14px', borderLeft: `4px solid ${log.action === 'create' ? '#1B5C3A' : log.action === 'delete' ? '#dc2626' : log.action === 'toggle' ? '#7c3aed' : '#F5A623'}`, borderTop: '1px solid #eee', borderBottom: '1px solid #eee', borderRight: '1px solid #eee' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 750, color: log.action === 'create' ? '#1B5C3A' : log.action === 'delete' ? '#dc2626' : log.action === 'toggle' ? '#7c3aed' : '#F5A623', textTransform: 'uppercase' }}>
                        {log.action === 'create' ? '➕ Created' : log.action === 'delete' ? '🗑️ Deleted' : log.action === 'toggle' ? '🔄 Toggled' : '✏️ Updated'}
                      </span>
                      <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>
                        {new Date(log.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} · {new Date(log.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>

                    {log.changes && Object.keys(log.changes).length > 0 && (
                      <div style={{ fontSize: 12, color: '#374151', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {Object.entries(log.changes).map(([field, { from, to }]) => (
                          <div key={field} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 0', borderBottom: '1px solid #eee' }}>
                            <span style={{ fontWeight: 700, minWidth: 100, color: '#6b7280' }}>{field}</span>
                            <span style={{ color: '#dc2626', textDecoration: 'line-through' }}>{JSON.stringify(from)}</span>
                            <span style={{ color: '#6b7280', fontWeight: 700 }}>→</span>
                            <span style={{ color: '#1B5C3A', fontWeight: 700 }}>{JSON.stringify(to)}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {log.action === 'create' && !log.changes && (
                      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>Filter created with initial configuration</div>
                    )}
                    {log.action === 'delete' && (
                      <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 500 }}>Filter permanently removed</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
